import React, { useEffect, useState, VFC } from "react"
import Modal from "../Modal"
import { useForm } from "react-hook-form"
import { useLocation } from "@reach/router"
import { getBgColor, Target } from "../../../utils"
import { navigate } from "gatsby"

const encode = data => {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&")
}

type Props = {
  target: Target
}

const ContactForm: VFC<Props> = ({ target }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPath, setCurrentPath] = useState("")
  const location = useLocation()

  useEffect(() => {
    if (location.search.includes("success")) {
      setIsModalOpen(true)
    }
    setCurrentPath(location.pathname)
  }, [location])

  const onSubmit = (data, e) => {
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": `${target}-contact`,
        ...data,
      }),
    })
      .then(() => navigate(e.target.getAttribute("action")))
      .catch(error => alert(error))
  }

  const closeModal = () => {
    setIsModalOpen(false)
    navigate(location.pathname)
  }

  return (
    <form
      className="mt-12 space-y-5 md:mt-16"
      name={`${target}-contact`}
      method="post"
      data-netlify="true"
      action={`/${target}/contact/?success`}
      netlify-honeypot="bot-field"
      onSubmit={handleSubmit(onSubmit)}
    >
      <input type="hidden" name="form-name" value={`${target}-contact`} />
      <label className="grid-cols-3 md:grid">
        <div className="flex mt-5 md:mt-0">
          <div>お名前</div>
          <div className="p-1 ml-5 text-xs text-red-500 border border-red-500 rounded-md h-fit">
            必須
          </div>
        </div>
        <div className="col-span-2">
          <input
            type="text"
            name="name"
            {...register("name", { required: true })}
            className="w-full p-2 mt-2 border border-gray-300 rounded-md md:mt-0"
          />
          {errors.name && (
            <div className="mt-1 text-sm text-red-400">
              項目を入力してください
            </div>
          )}
        </div>
      </label>
      <label className="grid-cols-3 md:grid">
        <div className="flex mt-5 md:mt-0">
          <div>ふりがな</div>
          <div className="p-1 ml-5 text-xs text-red-500 border border-red-500 rounded-md h-fit">
            必須
          </div>
        </div>
        <div className="col-span-2">
          <input
            type="text"
            name="name"
            {...register("kana", { required: true })}
            className="w-full col-span-2 p-2 mt-2 border border-gray-300 rounded-md md:mt-0"
          />
          {errors.kana && (
            <div className="mt-1 text-sm text-red-400">
              項目を入力してください
            </div>
          )}
        </div>
      </label>
      <label className="grid-cols-3 md:grid">
        <div className="flex mt-5 md:mt-0">
          <div>メールアドレス</div>
          <div className="p-1 ml-5 text-xs text-red-500 border border-red-500 rounded-md h-fit">
            必須
          </div>
        </div>
        <div className="col-span-2">
          <input
            type="email"
            name="email"
            {...register("email", { required: true })}
            className="w-full col-span-2 p-2 mt-2 border border-gray-300 rounded-md md:mt-0"
          />
          {errors.email && (
            <div className="mt-1 text-sm text-red-400">
              項目を入力してください
            </div>
          )}
        </div>
      </label>

      <label className="grid-cols-3 md:grid">
        <div className="mt-5 md:mt-0">電話番号</div>
        <input
          name="tel"
          type="number"
          className="w-full col-span-2 p-2 mt-2 border border-gray-300 rounded-md md:mt-0"
        />
      </label>
      <label className="grid-cols-3 md:grid">
        <div className="flex mt-5 md:mt-0">
          <div>お問い合わせ内容</div>
          <div className="p-1 ml-5 text-xs text-red-500 border border-red-500 rounded-md h-fit">
            必須
          </div>
        </div>
        <div className="col-span-2">
          <textarea
            name="message"
            {...register("message", { required: true, maxLength: 1000 })}
            rows={10}
            className="w-full col-span-2 p-2 mt-2 border border-gray-300 rounded-md md:mt-0"
          />
          {errors.message?.type === "required" && (
            <div className="mt-1 text-sm text-red-400">
              項目を入力してください
            </div>
          )}
          {errors.message?.type === "maxLength" && (
            <div className="mt-1 text-sm text-red-400">
              1000文字以内で入力してください
            </div>
          )}
        </div>
      </label>
      <button
        type="submit"
        className={`block px-10 py-3 mx-auto text-white rounded-md ${getBgColor(
          target
        )} font-kiwiMaru hover:opacity-60`}
      >
        送信する
      </button>
      <Modal isOpen={isModalOpen} closeModal={closeModal}>
        <h2>
          <span className="hidden md:block">お問い合わせ</span>
          ありがとうございます！
        </h2>
        <div className="mt-4">
          内容を確認して数日以内にご連絡差し上げます。
          <br />
          しばらくお待ちください。
        </div>
        <button
          onClick={closeModal}
          className={`block w-32 py-3 mx-auto mt-8 text-white rounded-md ${getBgColor(
            target
          )} hover:opacity-80`}
        >
          OK
        </button>
      </Modal>
    </form>
  )
}

export default ContactForm
